import { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Spin, Tabs, Select, Button, Table, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchDashboardStats, fetchRecentTickets, fetchAlerts, fetchTopParkings } from '../store/slices/dashboardSlice';
import { RevenueChart } from '../components/charts/RevenueChart';
import { 
  DollarOutlined, 
  CarOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  TrophyOutlined,
  RiseOutlined,
  AlertOutlined
} from '@ant-design/icons';

const { TabPane } = Tabs;

export function DashboardPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { stats, recentTickets, alerts, topParkings, isLoading } = useSelector((state: RootState) => state.dashboard);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [selectedParking, setSelectedParking] = useState('all');

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchRecentTickets(10));
    dispatch(fetchAlerts());
    dispatch(fetchTopParkings(5));
  }, [dispatch]);

  // Les données sont maintenant récupérées depuis l'API via Redux

  const columns = [
    { title: 'Plaque', dataIndex: 'plate', key: 'plate' },
    { title: 'Parking', dataIndex: 'parking', key: 'parking' },
    { 
      title: 'Statut', 
      dataIndex: 'status', 
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
          {status === 'ACTIVE' ? 'Actif' : 'Expiré'}
        </Tag>
      )
    },
    { title: 'Montant', dataIndex: 'amount', key: 'amount', render: (amount: number) => `${amount} TND` },
    { title: 'Heure', dataIndex: 'time', key: 'time' },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {/* En-tête avec filtres */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 m-0">Dashboard</h1>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Select
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              className="w-full sm:w-32"
            >
              <Select.Option value="1d">Aujourd'hui</Select.Option>
              <Select.Option value="7d">7 jours</Select.Option>
              <Select.Option value="30d">30 jours</Select.Option>
            </Select>
            <Select
              value={selectedParking}
              onChange={setSelectedParking}
              className="w-full sm:w-40"
            >
              <Select.Option value="all">Tous les parkings</Select.Option>
              <Select.Option value="centre">Centre Ville</Select.Option>
              <Select.Option value="mall">Mall</Select.Option>
              <Select.Option value="university">Université</Select.Option>
            </Select>
            <Button type="primary" icon={<RiseOutlined />} className="w-full sm:w-auto">
              <span className="hidden sm:inline">Exporter</span>
              <span className="sm:hidden">Export</span>
            </Button>
          </div>
        </div>
      </Card>

      {/* KPIs Principaux */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Revenus Aujourd'hui"
              value={stats?.revenueToday || 0}
              precision={2}
              suffix="TND"
              valueStyle={{ color: '#00AF00' }}
              prefix={<DollarOutlined />}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{ color: '#10B981', fontSize: '14px' }}>
                {stats?.ticketsToday || 0} tickets aujourd'hui
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tickets Actifs"
              value={stats?.activeTickets || 0}
              valueStyle={{ color: '#FFB900' }}
              prefix={<CarOutlined />}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{ color: '#F59E0B', fontSize: '14px' }}>
                {stats?.newUsersToday || 0} nouveaux utilisateurs
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tickets Totaux"
              value={stats?.totalTickets || 0}
              valueStyle={{ color: '#00C2C7' }}
              prefix={<UserOutlined />}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{ color: '#06B6D4', fontSize: '14px' }}>
                Tous les tickets confondus
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Durée Moyenne"
              value={stats?.avgTicketDuration || 0}
              suffix="min"
              valueStyle={{ color: '#722ED1' }}
              prefix={<ClockCircleOutlined />}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{ color: '#7C3AED', fontSize: '14px' }}>
                Durée moyenne des tickets
              </span>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Graphiques et Analyses */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Tabs defaultActiveKey="revenue">
            <TabPane tab="Revenus" key="revenue">
              <Card title="Évolution des Revenus" className="min-h-[300px] sm:min-h-[384px]">
                <RevenueChart />
              </Card>
            </TabPane>
            <TabPane tab="Performance" key="performance">
              <Card title="Performance des Parkings" className="min-h-[300px] sm:min-h-[384px]">
                <div className="flex flex-col gap-4">
                  {topParkings.map((parking, index) => (
                    <div key={parking.name} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 bg-neutral-50 rounded-lg">
                      <div className="flex items-center gap-4 flex-1 min-w-0 w-full sm:w-auto">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-bold text-sm">{index + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{parking.name}</div>
                          <div className="text-xs sm:text-sm text-neutral-600">{parking.tickets} tickets actifs</div>
                        </div>
                      </div>
                      <div className="text-left sm:text-right flex-shrink-0 w-full sm:w-auto pl-12 sm:pl-0">
                        <div className="font-bold text-green-600 text-sm">{parking.revenue.toFixed(2)} TND</div>
                        <div className="text-xs text-neutral-600">Revenus totaux</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabPane>
          </Tabs>
        </Col>
        
        <Col xs={24} lg={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Alertes */}
            <Card title="Alertes" size="small">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {alerts.length > 0 ? (
                  alerts.map((alert, index) => {
                    const getAlertStyle = (type: string) => {
                      switch (type) {
                        case 'error':
                          return { backgroundColor: '#FEF2F2', iconColor: '#EF4444' };
                        case 'warning':
                          return { backgroundColor: '#FFFBEB', iconColor: '#F59E0B' };
                        case 'success':
                          return { backgroundColor: '#EFF6FF', iconColor: '#3B82F6' };
                        default:
                          return { backgroundColor: '#F9FAFB', iconColor: '#6B7280' };
                      }
                    };
                    
                    const alertStyle = getAlertStyle(alert.type);
                    const IconComponent = alert.icon === 'AlertOutlined' ? AlertOutlined : 
                                        alert.icon === 'ClockCircleOutlined' ? ClockCircleOutlined : 
                                        alert.icon === 'TrophyOutlined' ? TrophyOutlined : AlertOutlined;
                    
                    return (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', backgroundColor: alertStyle.backgroundColor, borderRadius: '8px' }}>
                        <IconComponent style={{ color: alertStyle.iconColor }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: '500' }}>{alert.title}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280' }}>{alert.message}</div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', color: '#6B7280', padding: '20px' }}>
                    Aucune alerte pour le moment
                  </div>
                )}
              </div>
            </Card>

            {/* Top Parkings */}
            <Card title="Top Parkings" size="small">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {topParkings.map((parking, index) => (
                  <div key={parking.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                      <span style={{ width: '24px', height: '24px', backgroundColor: '#DBEAFE', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 }}>
                        {index + 1}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{parking.name}</span>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#10B981', marginLeft: '8px', flexShrink: 0 }}>{parking.revenue.toFixed(2)} TND</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Tableau des Tickets Récents */}
      <Card title="Tickets Récents">
        <div className="overflow-x-auto">
          <Table
            dataSource={recentTickets}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="id"
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>

      {/* Statistiques Supplémentaires */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Revenus 7 jours"
              value={stats?.revenue7d || 0}
              precision={2}
              suffix="TND"
              valueStyle={{ color: '#004C92' }}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{ color: '#2563EB', fontSize: '14px' }}>
                {stats?.revenue30d || 0} TND sur 30 jours
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Parkings"
              value={stats?.totalParkings || 0}
              valueStyle={{ color: '#722ED1' }}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{ color: '#7C3AED', fontSize: '14px' }}>
                {stats?.totalTickets || 0} tickets au total
              </span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Utilisateurs Totaux"
              value={stats?.totalUsers || 0}
              valueStyle={{ color: '#00C2C7' }}
            />
            <div style={{ marginTop: '8px' }}>
              <span style={{ color: '#06B6D4', fontSize: '14px' }}>
                {stats?.newUsersToday || 0} nouveaux aujourd'hui
              </span>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}